import TableView from 'src/components/dashboard/tables/TableView';
import { MBadge } from 'src/components/@material-extend';
import useAuth from 'src/hooks/useAuth';
import useOpenRowDataGrid from 'src/hooks/useOpenRowDataGrid';
import { useMemo } from 'react';
import { useOnboardingRequests } from 'src/hooks/corporate-onboarding/useOnboardingRequests';
import { useTranslation } from 'react-i18next';
import { parseUTC } from 'src/utils/timezones';
import { renderWeekdayDate } from 'src/utils/formatTime';
import { fPercent } from 'src/utils/formatNumber';
import { PATH_DASHBOARD } from 'src/routes/paths';
import {
  getOnboardingRequestProgress,
  getOnboardingRequestStatusLabel,
} from 'src/views/CorporateDashboard/Onboarding/common/requestStatus';

export function OnboardingRequestsBadge({ children }) {
  const { items } = useOnboardingRequests();

  const count = useMemo(() => {
    const incomplete = items.filter(
      ({ request_status, needed_items, completed_items }) =>
        request_status === 'pending' ||
        (request_status !== 'rejected' && completed_items < needed_items)
    );
    return incomplete.length;
  }, [items]);

  return (
    <MBadge badgeContent={count} color="info">
      {children}
    </MBadge>
  );
}

const useRequestPartnershipsColumns = () => {
  const { t, i18n } = useTranslation('translation');
  const { user } = useAuth();
  const makeOpenRowButton = useOpenRowDataGrid();

  return useMemo(
    () => [
      makeOpenRowButton((params) => {
        if (!params.row.can_view) {
          return null;
        }

        return (
          PATH_DASHBOARD.corporateOnboarding.request(
            params.row.compliance_profile_id,
            params.row.sender_team_id
          ) +
          `?type=NPO&gatekeeperType=${params.row.gatekeeper_type || 'CORPORATE'}`
        );
      }),
      {
        field: 'name',
        headerName: t('corporate-onboarding.gatekeeper-name', {
          defaultValue: 'Onboarding manager',
        }),
        width: 240,
        minWidth: 200,
      },
      {
        field: 'request_status',
        headerName: t('partnerships.status', {
          defaultValue: 'Status',
        }),
        width: 170,
        valueGetter: (_value, row) => getOnboardingRequestStatusLabel(t, row),
      },
      {
        field: 'compliance_profile_title',
        headerName: t('partnerships.compilance-profile'),
        width: 240,
        minWidth: 200,
      },
      {
        field: 'is_marked_compliant',
        headerName: t('partnerships.is-marked-compliant'),
        width: 180,
        minWidth: 150,
        valueGetter: (_value, row) => (row.is_marked_compliant ? 'Yes' : 'No'),
      },
      {
        field: 'no_businesses',
        headerName: t('partnerships.businesses'),
        type: 'number',
        width: 160,
        minWidth: 140,
      },
      {
        field: 'progress',
        type: 'number',
        headerName: t('partnerships.onboarding-progress'),
        width: 150,
        valueGetter: (_value, row) => getOnboardingRequestProgress(row),
        valueFormatter: (value) => (value == null ? '-' : fPercent(value)),
      },
      {
        field: 'created',
        type: 'date',
        headerName: t('partnerships.created'),
        width: 150,
        valueGetter: (_value, row) => parseUTC(row.created, user),
        renderCell: renderWeekdayDate,
      },
    ],
    [i18n.language]
  );
};

export default function Requests() {
  const { items, isLoading } = useOnboardingRequests();
  const COLUMNS = useRequestPartnershipsColumns();

  return (
    <TableView
      id="onboarding-requests-for-npo"
      getRowId={(row) =>
        row.intake_request_id ||
        `${row.sender_team_id}_${row.compliance_profile_id}`
      }
      rows={items}
      columns={COLUMNS}
      isLoading={isLoading}
      initialState={{
        pinnedColumns: {
          left: ['__view__'],
        },
      }}
    />
  );
}
